export type InsertedCadKind = 'part' | 'assembly'

const PART_CAD_FILE_PATTERN = /\.part(?:\s*\(\d+\))?\.cad(?:\s*\(\d+\))?(?:\.zip)?$/i
const ASSEMBLY_CAD_FILE_PATTERN = /\.assembly(?:\s*\(\d+\))?\.cad(?:\s*\(\d+\))?(?:\.zip)?$/i

/**
 * 归一化插入文件路径，确保 Windows 路径和后端 fileId 都能按同一规则识别扩展名。
 *
 * @param fileId 后端 fileId 或本地调试文件路径。
 * @returns 去除首尾空白并统一分隔符后的路径。
 */
export function normalizeInsertedCadFileId(fileId: string): string {
  return fileId.trim().replace(/\\/g, '/').replace(/^\.\/+/, '')
}

/**
 * 判断 fileId 是否指向插入 CAD 包。
 *
 * @param fileId 后端 fileId 或本地调试文件路径。
 * @returns 可识别时返回 true。
 */
export function isInsertedCadRuntimePackageFileId(fileId: string): boolean {
  const normalizedFileId = normalizeInsertedCadFileId(fileId)
  return PART_CAD_FILE_PATTERN.test(normalizedFileId) || ASSEMBLY_CAD_FILE_PATTERN.test(normalizedFileId)
}

/**
 * 只根据 fileId 扩展名判断插入 CAD 类型。
 *
 * 设计说明：runtime_manifest.json 字段不是稳定契约，只能作为包内索引使用；插入类型必须由用户选择的
 * 文件身份决定，避免后端临时字段变动导致零件/装配体语义漂移。
 *
 * @param fileId 后端 fileId 或本地调试文件路径。
 * @returns 插入 CAD 包类型。
 * @throws 当扩展名不是 `.part.cad` 或 `.assembly.cad` 时抛出明确错误。
 */
export function resolveInsertedCadKindFromFileId(fileId: string): InsertedCadKind {
  const normalizedFileId = normalizeInsertedCadFileId(fileId)
  if (PART_CAD_FILE_PATTERN.test(normalizedFileId)) {
    return 'part'
  }
  if (ASSEMBLY_CAD_FILE_PATTERN.test(normalizedFileId)) {
    return 'assembly'
  }

  throw new Error(
    `无法识别插入文件类型，仅支持 .part.cad 和 .assembly.cad：fileId=${normalizedFileId}`,
  )
}
